/**
 * Status Badge Component
 * Displays workflow status with appropriate icon and color
 */

import React from 'react';
import { useTheme, spacing, fontSize } from '@fti/design-system';

export type WorkflowStatusType = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface StatusBadgeProps {
  status: WorkflowStatusType | string;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

interface StatusConfig {
  icon: string;
  label: string;
  colorKey: 'success' | 'error' | 'warning' | 'info';
}

const STATUS_CONFIG: Record<WorkflowStatusType, StatusConfig> = {
  queued: {
    icon: '⏳',
    label: 'Queued',
    colorKey: 'warning',
  },
  running: {
    icon: '🔄',
    label: 'Running',
    colorKey: 'info',
  },
  completed: {
    icon: '✅',
    label: 'Completed',
    colorKey: 'success',
  },
  failed: {
    icon: '❌',
    label: 'Failed',
    colorKey: 'error',
  },
  cancelled: {
    icon: '🚫',
    label: 'Cancelled',
    colorKey: 'warning',
  },
};

/**
 * Badge component for displaying workflow status
 */
export function StatusBadge({
  status,
  size = 'md',
  showIcon = true,
}: StatusBadgeProps) {
  const { theme } = useTheme();

  // Normalize status to lowercase
  const normalizedStatus = status.toLowerCase() as WorkflowStatusType;
  const config = STATUS_CONFIG[normalizedStatus] || {
    icon: '❓',
    label: status,
    colorKey: 'warning' as const,
  };

  const color = theme.colors.semantic[config.colorKey].default;
  const bgColor = `${color}15`;

  const sizeStyles = {
    sm: {
      fontSize: fontSize.xs,
      padding: `2px ${spacing.xs}`,
      iconSize: fontSize.xs,
    },
    md: {
      fontSize: fontSize.xs,
      padding: `${spacing.xs} ${spacing.sm}`,
      iconSize: fontSize.sm,
    },
    lg: {
      fontSize: fontSize.sm,
      padding: `${spacing.sm} ${spacing.md}`,
      iconSize: fontSize.base,
    },
  };

  const styles = sizeStyles[size];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: styles.padding,
        borderRadius: '4px',
        background: bgColor,
        color: color,
        fontSize: styles.fontSize,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {showIcon && (
        <span style={{ fontSize: styles.iconSize }}>{config.icon}</span>
      )}
      {config.label}
    </span>
  );
}

export default StatusBadge;
